import { useState } from 'react';
import { useFriends } from '../hooks/useFriends';
import AddFriend from '../components/AddFriend';

// MUI components
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Email as EmailIcon
} from '@mui/icons-material';

const Friends = () => {
  const { friends, friendRequests, respondToFriendRequest } = useFriends();
  const [tabValue, setTabValue] = useState(0);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleRespondToRequest = async (requestId, accept) => {
    try {
      await respondToFriendRequest(requestId, accept);
    } catch (err) {
      console.error('Error responding to friend request:', err);
    }
  };
  
  // Filter friend requests by direction
  const incomingRequests = friendRequests.filter(req => req.direction === 'incoming');
  const outgoingRequests = friendRequests.filter(req => req.direction === 'outgoing');
  
  return (
    <Container maxWidth="md">
      <Box my={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Friends
        </Typography>
        
        {/* Add Friend Form */}
        <AddFriend />
        
        {/* Tabs for Friends and Requests */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={`Friends (${friends.length})`} />
            <Tab 
              label={
                <>
                  Requests 
                  {incomingRequests.length > 0 && (
                    <Chip 
                      label={incomingRequests.length} 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </>
              } 
            />
          </Tabs>
          
          {/* Friends Tab */}
          {tabValue === 0 && (
            <Box p={2}>
              {friends.length > 0 ? (
                <List>
                  {friends.map((friend) => (
                    <ListItem key={friend.id}>
                      <ListItemAvatar>
                        <Avatar>
                          {friend.friend?.display_name?.charAt(0).toUpperCase() || <PersonIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={friend.friend?.display_name || 'Unknown User'}
                        secondary={friend.friend?.email}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="email">
                          <EmailIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body1" align="center" sx={{ py: 3 }}>
                  You don't have any friends yet. Add some friends to get started!
                </Typography>
              )}
            </Box>
          )}
          
          {/* Requests Tab */}
          {tabValue === 1 && (
            <Box p={2}>
              {incomingRequests.length > 0 || outgoingRequests.length > 0 ? (
                <>
                  {incomingRequests.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Incoming Requests
                      </Typography>
                      <List>
                        {incomingRequests.map((request) => (
                          <ListItem key={request.id}>
                            <ListItemAvatar>
                              <Avatar>
                                {request.requester?.display_name?.charAt(0).toUpperCase() || <PersonIcon />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={request.requester?.display_name || 'Unknown User'}
                              secondary={request.requester?.email}
                            />
                            <ListItemSecondaryAction>
                              <IconButton 
                                edge="end" 
                                aria-label="accept" 
                                color="success"
                                onClick={() => handleRespondToRequest(request.id, true)}
                                sx={{ mr: 1 }}
                              >
                                <CheckIcon />
                              </IconButton>
                              <IconButton 
                                edge="end" 
                                aria-label="reject" 
                                color="error"
                                onClick={() => handleRespondToRequest(request.id, false)}
                              >
                                <CloseIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                  
                  {incomingRequests.length > 0 && outgoingRequests.length > 0 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                  
                  {outgoingRequests.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>
                        Outgoing Requests
                      </Typography>
                      <List>
                        {outgoingRequests.map((request) => (
                          <ListItem key={request.id}>
                            <ListItemAvatar>
                              <Avatar>
                                {request.recipient?.display_name?.charAt(0).toUpperCase() || <PersonIcon />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={request.recipient?.display_name || 'Unknown User'}
                              secondary={request.recipient?.email}
                            />
                            <ListItemSecondaryAction>
                              <Chip label="Pending" color="warning" size="small" />
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body1" align="center" sx={{ py: 3 }}>
                  No friend requests at the moment.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Friends; 